import { type ReactNode } from 'react';
import { type ProductDetail } from '@hungryroot/rest';
import { type AccessedFrom, type Location } from '@hungryroot/segment';
import { Header } from '../Header/Header';
import { ProductLoader } from '../ProductLoader/ProductLoader';
import { SimplePDPModalContents } from '../SimplePDPModal/SimplePDPModalContents';
import {
  type LeftIconProps,
  type SimplePDPModalProps,
} from '../SimplePDPModal/types';
import { XButton } from '../XButton/XButton';
import { type GetIconsArgs, Icons } from './PDPModalIcons';

/**
 * Overrides for `productInteraction` / `pairingInteraction` events fired from inside the PDP.
 * When a field is `undefined`, the default (location='pdp', unset page_name/accessed_from/conversation_id)
 * is used. Pass `location: null` to explicitly omit the location property.
 */
export type PDPInteractionOverrides = {
  location?: Location | null;
  pageName?: string;
  accessedFrom?: AccessedFrom;
  conversationId?: string;
};

/**
 * Props for stacking the modal - if stacked is true, we need an action for removing the modal from the stack
 * If we dynamically set `stacked`, we also require the action to handle the `true` case
 * */
export type StackProps =
  | {
      stacked: false;
    }
  | {
      stacked: boolean;
      onBackClick: () => void;
    };

type FooterProps =
  | {
      footerButton: ReactNode;
    }
  | {
      createFooterButton?: (
        product?: ProductDetail,
        handlers?: {
          onAdd: () => void;
          onRemove: () => void;
          onIncrement: () => void;
          onSubtract: () => void;
        }
      ) => ReactNode;
    };

type IconProps = {
  excludeIcons?: GetIconsArgs['exclusions'];
  searchAttribution?: GetIconsArgs['searchAttribution'];
};

export type PDPModalProps = Omit<
  SimplePDPModalProps,
  keyof LeftIconProps | 'footerButton'
> &
  StackProps &
  FooterProps &
  IconProps & {
    interactionOverrides?: PDPInteractionOverrides;
  };

const getFooterButton = (
  props: FooterProps,
  product?: ProductDetail
): ReactNode => {
  if ('createFooterButton' in props && props.createFooterButton) {
    return props.createFooterButton(product);
  }

  if ('footerButton' in props) {
    return props.footerButton;
  }
};

export function PDPModal({ id, ...props }: PDPModalProps) {
  const { interactionOverrides } = props;
  return (
    <ProductLoader id={id}>
      {({ product: productv2 }) => {
        return (
          <SimplePDPModalContents
            {...props}
            onImageScroll={props.onImageScroll}
            onProductContentScroll={props.onProductContentScroll}
            onShowMoreNutrition={props.onShowMoreNutrition}
            testid={props.testid ?? 'pdp-modal'}
            footerButton={getFooterButton(props, productv2)}
            primaryTopIconButton={
              <Icons
                product={productv2}
                scrolled={false}
                exclusions={props.excludeIcons}
                interactionOverrides={interactionOverrides}
                {...props}
              />
            }
            productV2={productv2}
            header={
              <Header
                primaryTopIconButton={
                  <Icons
                    product={productv2}
                    scrolled={true}
                    exclusions={props.excludeIcons}
                    interactionOverrides={interactionOverrides}
                    {...props}
                  />
                }
                primaryTopRightIconButton={
                  <XButton
                    onClick={() => {
                      props.onProductXClick?.();
                      props.onDismiss();
                    }}
                    size="medium"
                    prominence="tertiary"
                    style="muted"
                  />
                }
              />
            }
            badgesOverride={props.badgesOverride}
            badgeLabel={props.badgeLabel}
            isPdpBadgeLabelResolved={props.isPdpBadgeLabelResolved}
          />
        );
      }}
    </ProductLoader>
  );
}
